import { useMutation } from "@tanstack/react-query";
import { PRODUCT_AREA_ASSET_ID, Product } from "@/shared/models/Product";
import { PunchBeamPosition } from "@/shared/models/PunchBeamPosition";
import { PunchPerforation } from "@/shared/models/PunchPerforation";
import {
	createProduct,
	deleteProduct,
	setProductParent,
	updateProduct,
} from "@/shared/repositories/ProductRepository";
import {
	createFabricationSpecs,
	updateFabricationSpecs,
} from "@/shared/repositories/FabricationSpecsRepository";
import {
	createSawSpecs,
	updateSawSpecs,
} from "@/shared/repositories/SawSpecsRepository";
import {
	createPunchSpecs,
	updatePunchSpecs,
} from "@/shared/repositories/PunchSpecsRepository";
import { setPunchBeamPositions } from "@/shared/repositories/PunchBeamPositionsRepository";
import { setPunchPerforations } from "@/shared/repositories/PunchPerforationsRepository";
import { upsertFinishSpecs } from "@/shared/repositories/FinishSpecsRepository";

const PRODUCT_FIELDS = [
	"PartNumber",
	"ExtendedPartNumber",
	"CustomerPartNumber",
	"GenericName",
	"CustomerId",
	"SectionId",
	"GradeId",
	"IsActive",
	"Comments",
] as const;

const WELD_SPECS_FIELD_MAP: Record<string, string> = {
	FinishedLength: "FinishedLength",
	Tolerance: "Tolerance",
	Program: "Program",
	CleaningCycle: "CleaningCycle",
	BottomKeeper: "BottomKeeper",
	BundleCol: "BundleCol",
	BundleRow: "BundleRow",
	BundleCount: "BundleCount",
	StickSize: "StickSize",
	WeldComments: "Comments",
	TestCycleInterval: "TestCycleInterval",
	DrawingPath: "DrawingPath",
	DrawingNumber: "DrawingNumber",
	ClipId: "ClipId",
	PaintCutTypeId: "PaintCutTypeId",
	EntryKeeperId: "EntryKeeperId",
	ExitKeeperId: "ExitKeeperId",
	SideGage: "SideGage",
	HoleGage: "HoleGage",
};

const PAINT_SPECS_FIELD_MAP: Record<string, string> = {
	FinishedLength: "FinishedLength",
	Tolerance: "Tolerance",
	PaintCutTypeId: "PaintCutTypeId",
	SpecialInstructions: "SpecialInstructions",
	BundleCol: "BundleCol",
	BundleRow: "BundleRow",
	BundleCount: "BundleCount",
	StickSize: "StickSize",
	DrawingPath: "DrawingPath",
	DrawingNumber: "DrawingNumber",
};

const PUNCH_SPECS_FIELD_MAP: Record<string, string> = {
	FinishedLength: "FinishedLength",
	BandUp: "BandUp",
	BundleCol: "BundleCol",
	BundleRow: "BundleRow",
	BundleCount: "BundleCount",
	StickSize: "StickSize",
};

const PUNCH_TABLE_FIELD_MAP: Record<string, string> = {
	UpperRiserDrawingNum: "UpperRiserDrawingNum",
	LowerRiserDrawingNum: "LowerRiserDrawingNum",
	StopBlockNumber: "StopBlockNumber",
	DrawingNumber: "DrawingNumber",
	DrawingPath: "DrawingPath",
};

const WAX_SPECS_FIELD_MAP: Record<string, string> = {
	BundleCol: "BundleCol",
	BundleRow: "BundleRow",
	BundleCount: "BundleCount",
	StickSize: "StickSize",
	DrawingPath: "DrawingPath",
	DrawingNumber: "DrawingNumber",
};

const SAW_SPECS_FIELD_MAP: Record<string, string> = {
	TargetLength: "TargetLength",
	TopTolerance: "TopTolerance",
	BottomTolerance: "BottomTolerance",
	FinishPcB: "FinishPcB",
	MillLength: "MillLength",
	MillPcB: "MillPcB",
};

const GALV_SPECS_FIELD_MAP: Record<string, string> = {
	BundleCol: "BundleCol",
	BundleRow: "BundleRow",
	BundleCount: "BundleCount",
	StickSize: "StickSize",
};

function getFinishSpecsFieldMap(
	areaId: number,
	sawsId: number,
	waxId: number,
): Record<string, string> {
	const base: Record<string, string> = { BandLength: "BandLength" };
	if (areaId === sawsId) {
		return { ...base, Banded: "Banded", NumberOfBands: "NumberOfBands" };
	}
	if (areaId === PRODUCT_AREA_ASSET_ID["galv-sawed"]) {
		return {
			...base,
			Banded: "Banded",
			NumberOfBands: "NumberOfBands",
			GroovedTimber: "GroovedTimber",
		};
	}
	if (areaId === waxId) {
		return {
			...base,
			GroovedTimber: "GroovedTimber",
			Cardboard: "Cardboard",
		};
	}
	return { ...base, Banded: "Banded", GroovedTimber: "GroovedTimber" };
}

type UseProductMutationsOptions = {
	getDirtyValues: () => Partial<Product>;
	activeAreaId: number;
	weldsAreaId: number;
	sawsAreaId: number;
	paintAreaId: number;
	punchAreaId: number;
	waxAreaId: number;
	onSuccess: (
		message: string,
		productId?: number,
		droppedRows?: number,
	) => void;
	onError: (error: string) => void;
};

export type ProductCreateInput = Product & {
	parentProductId?: number | null;
};

function getFieldMapForArea(
	areaId: number,
	weldsId: number,
	paintId: number,
	punchId: number,
	waxId: number,
): Record<string, string> | null {
	switch (areaId) {
		case weldsId:
			return WELD_SPECS_FIELD_MAP;
		case paintId:
			return PAINT_SPECS_FIELD_MAP;
		case punchId:
			return PUNCH_SPECS_FIELD_MAP;
		case waxId:
			return WAX_SPECS_FIELD_MAP;
		case PRODUCT_AREA_ASSET_ID["galv-punched"]:
		case PRODUCT_AREA_ASSET_ID["galv-welded"]:
			return GALV_SPECS_FIELD_MAP;
		default:
			return null; // saw / galv-sawed: no FabricationSpecs
	}
}

export function useProductMutations({
	getDirtyValues,
	activeAreaId,
	weldsAreaId,
	sawsAreaId,
	paintAreaId,
	punchAreaId,
	waxAreaId,
	onSuccess,
	onError,
}: UseProductMutationsOptions) {
	const deleteMutation = useMutation({
		mutationFn: (productId: number) => deleteProduct(productId),
		onSuccess: (response) => {
			if (response.ok) {
				onSuccess("The product was deleted successfully");
			} else {
				onError(response.message);
			}
		},
		onError: (error: Error) => {
			onError(error.message);
		},
	});

	const upsertMutation = useMutation({
		mutationFn: async (data: ProductCreateInput) => {
			const isCreate = data.ProductId === null;
			const dirtyValues = getDirtyValues();

			if (isCreate) {
				return handleCreate(
					data,
					activeAreaId,
					weldsAreaId,
					sawsAreaId,
					paintAreaId,
					punchAreaId,
					waxAreaId,
				);
			}

			return handleUpdate(
				data.ProductId as number,
				data,
				dirtyValues,
				activeAreaId,
				weldsAreaId,
				sawsAreaId,
				paintAreaId,
				punchAreaId,
				waxAreaId,
			);
		},
		onSuccess: (response) => {
			if (response.ok) {
				const createdProductId =
					"data" in response
						? (response.data?.tables?.[0]?.rows?.[0]?.ProductId as
								| number
								| undefined)
						: undefined;
				const droppedRows =
					"droppedRows" in response && typeof response.droppedRows === "number"
						? response.droppedRows
						: 0;
				onSuccess(
					"The product was saved successfully",
					createdProductId,
					droppedRows,
				);
			} else {
				onError(response.message);
			}
		},
		onError: (error: Error) => {
			onError(error.message);
		},
	});

	return {
		deleteMutation,
		upsertMutation,
		isPending: deleteMutation.isPending || upsertMutation.isPending,
	};
}

async function handleCreate(
	data: ProductCreateInput,
	activeAreaId: number,
	weldsAreaId: number,
	sawsAreaId: number,
	paintAreaId: number,
	punchAreaId: number,
	waxAreaId: number,
) {
	const productResponse = await createProduct({
		...data,
		AreaAssetId: activeAreaId,
	});
	if (!productResponse.ok) {
		return productResponse;
	}

	const productId = productResponse.data?.tables?.[0]?.rows?.[0]?.ProductId;
	if (!productId) return productResponse;

	const isSawArea = activeAreaId === sawsAreaId;
	const isPunchArea = activeAreaId === punchAreaId;
	const fieldMap = getFieldMapForArea(
		activeAreaId,
		weldsAreaId,
		paintAreaId,
		punchAreaId,
		waxAreaId,
	);

	if (fieldMap) {
		const fabFields = extractFieldsFromProduct(data, fieldMap);
		const fabResponse = await createFabricationSpecs({
			ProductId: productId,
			AreaAssetId: activeAreaId,
			...fabFields,
		});
		if (!fabResponse.ok) return fabResponse;
	} else if (isSawArea) {
		const sawFields = extractFieldsFromProduct(data, SAW_SPECS_FIELD_MAP);
		const sawResponse = await createSawSpecs({
			ProductId: productId,
			...sawFields,
		});
		if (!sawResponse.ok) return sawResponse;
	}

	let droppedRows = 0;

	if (isPunchArea) {
		const punchTableFields = extractFieldsFromProduct(
			data,
			PUNCH_TABLE_FIELD_MAP,
		);
		const punchResponse = await createPunchSpecs({
			ProductId: productId,
			...punchTableFields,
		});
		if (!punchResponse.ok) return punchResponse;

		const beamNorm = normalizeBeamPositions(data.BeamPositions);
		droppedRows += beamNorm.droppedCount;
		const beamResponse = await setPunchBeamPositions(productId, beamNorm.rows);
		if (!beamResponse.ok) return beamResponse;

		const perfNorm = normalizePerforations(data.Perforations);
		droppedRows += perfNorm.droppedCount;
		const perfResponse = await setPunchPerforations(productId, perfNorm.rows);
		if (!perfResponse.ok) return perfResponse;
	}

	const finishMap = getFinishSpecsFieldMap(activeAreaId, sawsAreaId, waxAreaId);
	const finishFields = extractFieldsFromProduct(data, finishMap);
	// When Banded is unchecked, BandLength and NumberOfBands are meaningless —
	// force null on save even if the form preserved their displayed values.
	if (data.Banded === false) {
		if ("BandLength" in finishMap) finishFields.BandLength = null;
		if ("NumberOfBands" in finishMap) finishFields.NumberOfBands = null;
	}
	if (Object.keys(finishFields).length > 0) {
		const finishResponse = await upsertFinishSpecs(productId, finishFields);
		if (!finishResponse.ok) return finishResponse;
	}

	if (data.parentProductId != null) {
		const parentResponse = await setProductParent(
			productId,
			data.parentProductId,
		);
		if (!parentResponse.ok) return parentResponse;
	}

	return { ...productResponse, droppedRows };
}

async function handleUpdate(
	productId: number,
	data: Product,
	dirtyValues: Partial<Product>,
	activeAreaId: number,
	weldsAreaId: number,
	sawsAreaId: number,
	paintAreaId: number,
	punchAreaId: number,
	waxAreaId: number,
) {
	const dirtyProductFields = extractDirtyProductFields(dirtyValues);
	if (activeAreaId !== sawsAreaId) {
		delete dirtyProductFields.SectionId;
		delete dirtyProductFields.GradeId;
	}
	if (Object.keys(dirtyProductFields).length > 0) {
		const productResponse = await updateProduct(productId, dirtyProductFields);
		if (!productResponse.ok) return productResponse;
	}

	const isPunchArea = activeAreaId === punchAreaId;
	const fieldMap = getFieldMapForArea(
		activeAreaId,
		weldsAreaId,
		paintAreaId,
		punchAreaId,
		waxAreaId,
	);

	if (fieldMap) {
		const dirtyFabFields = extractDirtyFields(dirtyValues, fieldMap);
		if (Object.keys(dirtyFabFields).length > 0) {
			const fabResponse = await updateFabricationSpecs(
				productId,
				activeAreaId,
				dirtyFabFields,
			);
			if (!fabResponse.ok) return fabResponse;
		}
	}

	const dirtySawFields = extractDirtyFields(dirtyValues, SAW_SPECS_FIELD_MAP);
	if (Object.keys(dirtySawFields).length > 0) {
		const sawResponse = await updateSawSpecs(productId, dirtySawFields);
		if (!sawResponse.ok) return sawResponse;
	}

	const finishMap = getFinishSpecsFieldMap(activeAreaId, sawsAreaId, waxAreaId);
	const dirtyFinishFields = extractDirtyFields(dirtyValues, finishMap);
	// When the user just unchecked Banded, BandLength/NumberOfBands are
	// meaningless — force null on save even if those fields aren't dirty.
	if ("Banded" in dirtyValues && dirtyValues.Banded === false) {
		if ("BandLength" in finishMap) dirtyFinishFields.BandLength = null;
		if ("NumberOfBands" in finishMap) dirtyFinishFields.NumberOfBands = null;
	}
	if (Object.keys(dirtyFinishFields).length > 0) {
		const finishResponse = await upsertFinishSpecs(
			productId,
			dirtyFinishFields,
		);
		if (!finishResponse.ok) return finishResponse;
	}

	let droppedRows = 0;

	if (isPunchArea) {
		const dirtyPunchTableFields = extractDirtyFields(
			dirtyValues,
			PUNCH_TABLE_FIELD_MAP,
		);
		if (Object.keys(dirtyPunchTableFields).length > 0) {
			const punchResponse = await updatePunchSpecs(
				productId,
				dirtyPunchTableFields,
			);
			if (!punchResponse.ok) return punchResponse;
		}

		const beamNorm = normalizeBeamPositions(data.BeamPositions);
		droppedRows += beamNorm.droppedCount;
		const beamResponse = await setPunchBeamPositions(productId, beamNorm.rows);
		if (!beamResponse.ok) return beamResponse;

		const perfNorm = normalizePerforations(data.Perforations);
		droppedRows += perfNorm.droppedCount;
		const perfResponse = await setPunchPerforations(productId, perfNorm.rows);
		if (!perfResponse.ok) return perfResponse;
	}

	return { ok: true, message: "Product updated successfully", droppedRows };
}

type Normalized<T> = { rows: T[]; droppedCount: number };

function normalizeBeamPositions(
	positions: PunchBeamPosition[] | undefined,
): Normalized<{
	PunchBeamPositionId: number | null;
	Plate: PunchBeamPosition["Plate"];
	PositionOrder: number;
	PositionValue: string;
}> {
	if (!positions) return { rows: [], droppedCount: 0 };
	const rows = positions
		.filter(
			(p): p is PunchBeamPosition =>
				p != null && !!p.PositionValue && p.PositionValue.trim() !== "",
		)
		.map(({ PunchBeamPositionId, Plate, PositionOrder, PositionValue }) => ({
			PunchBeamPositionId,
			Plate,
			PositionOrder,
			PositionValue,
		}));
	return { rows, droppedCount: 0 };
}

function isPerforationFilled(p: PunchPerforation): boolean {
	return (
		p.HoleSize !== null ||
		(p.DetailNumber !== null && p.DetailNumber.trim() !== "") ||
		(p.PunchPartNumber !== null && p.PunchPartNumber.trim() !== "") ||
		(p.ButtonPartNumber !== null && p.ButtonPartNumber.trim() !== "")
	);
}

function normalizePerforations(
	perforations: PunchPerforation[] | undefined,
): Normalized<{
	PunchPerforationId: number | null;
	PerforationType: PunchPerforation["PerforationType"];
	HoleSize: number | null;
	DetailNumber: string | null;
	PunchPartNumber: string | null;
	ButtonPartNumber: string | null;
}> {
	if (!perforations) return { rows: [], droppedCount: 0 };
	let droppedCount = 0;
	const rows: Array<{
		PunchPerforationId: number | null;
		PerforationType: PunchPerforation["PerforationType"];
		HoleSize: number | null;
		DetailNumber: string | null;
		PunchPartNumber: string | null;
		ButtonPartNumber: string | null;
	}> = [];
	for (const p of perforations) {
		if (p == null) continue;
		if (!isPerforationFilled(p)) {
			// Wiped existing rows go to the SP so it can delete them; brand-new
			// empty rows (id=null) are silently discarded.
			if (p.PunchPerforationId == null) droppedCount += 1;
			continue;
		}
		rows.push({
			PunchPerforationId: p.PunchPerforationId,
			PerforationType: p.PerforationType,
			HoleSize: p.HoleSize,
			DetailNumber: p.DetailNumber,
			PunchPartNumber: p.PunchPartNumber,
			ButtonPartNumber: p.ButtonPartNumber,
		});
	}
	return { rows, droppedCount };
}

function extractFieldsFromProduct(
	data: Product,
	fieldMap: Record<string, string>,
): Record<string, unknown> {
	const fields: Record<string, unknown> = {};
	for (const [formField, dbField] of Object.entries(fieldMap)) {
		const value = data[formField as keyof Product];
		if (value !== null && value !== undefined && value !== "") {
			fields[dbField] = value;
		}
	}
	return fields;
}

function extractDirtyProductFields(
	dirtyValues: Partial<Product>,
): Record<string, string | number | boolean | null> {
	const fields: Record<string, string | number | boolean | null> = {};
	for (const field of PRODUCT_FIELDS) {
		if (field in dirtyValues) {
			fields[field] = dirtyValues[field as keyof typeof dirtyValues] as
				| string
				| number
				| boolean
				| null;
		}
	}
	return fields;
}

function extractDirtyFields(
	dirtyValues: Partial<Product>,
	fieldMap: Record<string, string>,
): Record<string, string | number | boolean | null> {
	const fields: Record<string, string | number | boolean | null> = {};
	for (const [formField, dbField] of Object.entries(fieldMap)) {
		if (formField in dirtyValues) {
			fields[dbField] = dirtyValues[formField as keyof typeof dirtyValues] as
				| string
				| number
				| boolean
				| null;
		}
	}
	return fields;
}
